import {
  vitrineDonoSchema,
  donoWantedSchema,
  donoLinkedSchema,
  donoHojeSchema,
  donoCrefSchema,
  donoCustosSchema,
  donoDinheiroSchema,
  donoKpiSchema,
  donoPessoaSchema,
  donoSegurancaSchema,
  donoSuporteSchema,
  donoUsoSchema,
  screenSiteSchema,
  type DonoCrefMark,
  type DonoCustoFixo,
} from '@secretario/shared';
import { z } from 'zod';
import { saveBlob } from '../native.js';
import { apiBinary, apiFetch } from './http.js';

/** Painel do dono: agregados do negócio (só a conta do dono recebe). */
/** Esta conta é a do dono? (Os outros recebem 404 e não veem a entrada do painel.) */
export async function isDono(): Promise<boolean> {
  try {
    await apiFetch<unknown>('/v1/dono/acesso');
    return true;
  } catch {
    return false;
  }
}
export const getDonoCref = async () =>
  z.object({ crefs: z.array(donoCrefSchema) }).parse(await apiFetch<unknown>('/v1/dono/cref'))
    .crefs;
export const markDonoCref = async (input: DonoCrefMark) =>
  z
    .object({ crefs: z.array(donoCrefSchema) })
    .parse(await apiFetch<unknown>('/v1/dono/cref', { method: 'POST', body: input })).crefs;
export const getDonoHoje = async () =>
  donoHojeSchema.parse(await apiFetch<unknown>('/v1/dono/hoje'));
export const getDonoKpi = async () =>
  donoKpiSchema.parse(await apiFetch<unknown>('/v1/dono/visao'));
export const getDonoPessoas = async (busca: string) =>
  z
    .object({ pessoas: z.array(donoPessoaSchema) })
    .parse(await apiFetch<unknown>(`/v1/dono/pessoas?busca=${encodeURIComponent(busca)}`)).pessoas;
export const getDonoDinheiro = async () =>
  donoDinheiroSchema.parse(await apiFetch<unknown>('/v1/dono/dinheiro'));
export const getDonoUso = async () => donoUsoSchema.parse(await apiFetch<unknown>('/v1/dono/uso'));
export const getDonoCustos = async () =>
  donoCustosSchema.parse(await apiFetch<unknown>('/v1/dono/custos'));
export const getDonoSuporte = async () =>
  donoSuporteSchema.parse(await apiFetch<unknown>('/v1/dono/suporte'));

/** Baixa uma aba em CSV (ponto e vírgula, abre no Excel). */
export async function downloadDonoCsv(tab: 'pessoas' | 'custos' | 'dinheiro' | 'suporte') {
  const response = await apiBinary(`/v1/dono/csv/${tab}`);
  await saveBlob(await response.blob(), `secretario-dono-${tab}.csv`);
}

export const getDonoSeguranca = async () =>
  donoSegurancaSchema.parse(await apiFetch<unknown>('/v1/dono/seguranca'));
export const addDonoCusto = (input: {
  label: string;
  vendor: string;
  amountCents: number;
  recurrence: 'mensal' | 'anual';
}) => apiFetch<{ fixos: DonoCustoFixo[] }>('/v1/dono/custos', { method: 'POST', body: input });
export const removeDonoCusto = (id: string) =>
  apiFetch<void>(`/v1/dono/custos/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ── Performance (aba do painel e relatório diário) ──
const perfDay = z.object({
  day: z.string(),
  novos: z.number(),
  ativos: z.number(),
  conversas: z.number(),
  vozMin: z.number(),
  receitaCents: z.number(),
  custoIaCents: z.number(),
});
export type DonoPerfDay = z.infer<typeof perfDay>;
export const donoPerformanceSchema = z.object({
  geradoEm: z.string(),
  dia: z.string(),
  ontem: perfDay,
  anteontem: perfDay,
  media7: perfDay,
  serie: z.array(perfDay),
  mes: z.object({
    receitaCents: z.number(),
    custoIaCents: z.number(),
    fixosCents: z.number(),
    liquidoCents: z.number(),
    novos: z.number(),
  }),
  contas: z.object({ total: z.number(), semNumero: z.number() }),
  kpi: donoKpiSchema,
  eventos: z.array(z.object({ quando: z.string(), texto: z.string(), kind: z.string() })),
  saude: z.object({
    discoTotalBytes: z.number().nullable(),
    discoLivreBytes: z.number().nullable(),
    memoriaTotalBytes: z.number(),
    memoriaLivreBytes: z.number(),
    carga: z.array(z.number()),
    nucleos: z.number(),
    servidorNoArS: z.number(),
    apiNoArS: z.number(),
    bancoBytes: z.number(),
    maioresTabelas: z.array(z.object({ nome: z.string(), bytes: z.number() })),
    iaUltimaHoraUsd: z.number(),
    iaTetoHoraUsd: z.number().nullable(),
    falhasEntrada24h: z.number(),
    pedidosSuporte24h: z.number(),
    pagamentosRecusados7d: z.number(),
    /** O tempo de resposta do Secretário (24 h); apps de antes da F0 não recebem. */
    respostas: z
      .object({
        pedidos: z.number(),
        toques: z.number(),
        atalhos: z.number().optional(),
        primeiroMs: z.number(),
        totalMs: z.number(),
        totalP90Ms: z.number(),
        falhas: z.number(),
      })
      .nullable()
      .optional(),
  }),
  atencao: z.array(z.string()),
  positivos: z.array(z.string()),
  ideias: z.array(z.string()),
});
export type DonoPerformance = z.infer<typeof donoPerformanceSchema>;
export const getDonoPerformance = async () =>
  donoPerformanceSchema.parse(await apiFetch<unknown>('/v1/dono/performance'));
/** Gera o relatório do dia agora (vai para Documentos e chega na conversa). */
export const gerarRelatorioDono = async () =>
  z
    .object({ documents: z.array(z.string()) })
    .parse(await apiFetch<unknown>('/v1/dono/relatorio', { method: 'POST' }));

/** Gera o memorando da semana agora (por diretoria; sai sozinho toda segunda às 7h). */
export const gerarMemorandoDono = async () =>
  z
    .object({ documents: z.array(z.string()) })
    .parse(await apiFetch<unknown>('/v1/dono/memorando', { method: 'POST' }));

/** Sites que abrem na tela para todos (a lista do dono). */
export const getDonoSites = async () =>
  z.object({ sites: z.array(screenSiteSchema) }).parse(await apiFetch<unknown>('/v1/dono/sites'))
    .sites;
export const addDonoSite = async (site: string) =>
  screenSiteSchema.parse(
    await apiFetch<unknown>('/v1/dono/sites', { method: 'POST', body: { site } }),
  );
export const removeDonoSite = (id: string) =>
  apiFetch<unknown>(`/v1/dono/sites/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Entrada pelo negócio (estudo da cortesia, C2). */
export const getDonoLinked = async () =>
  donoLinkedSchema.parse(await apiFetch<unknown>('/v1/dono/entrada'));

/** Procurados (G6): os lugares mais pedidos no mês, para convidar. */
export const getDonoWanted = async () =>
  donoWantedSchema.parse(await apiFetch<unknown>('/v1/dono/procurados'));

/** Vitrine do bairro (G3): conferência, fila e decisão. */
export const getDonoVitrine = async (review: 'pendente' | 'aprovado' | 'recusado') =>
  vitrineDonoSchema.parse(await apiFetch<unknown>(`/v1/dono/vitrine?review=${review}`));
export const queueDonoVitrine = async (city: string, limit: number) =>
  z
    .object({ added: z.number().int() })
    .parse(
      await apiFetch<unknown>('/v1/dono/vitrine/fila', { method: 'POST', body: { city, limit } }),
    ).added;
export const decideDonoVitrine = async (placeId: string, decision: 'aprovado' | 'recusado') =>
  apiFetch<unknown>(`/v1/dono/vitrine/${placeId}/decisao`, { method: 'POST', body: { decision } });
